import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Calculate as CalculateIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Replay as ReplayIcon,
  NoteAdd as NoteAddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { KnowledgeTraceView } from '../components/learning/common';
import { ReliabilityTraceView } from '../components/reliability/ReliabilityTraceView';
import { JsonViewer } from '../components/JsonViewer';
import { GroundingMetadataPanel } from '../components/GroundingMetadataPanel';
import { BatchStatusPanel, isBatchStatusOutput } from '../components/BatchStatusPanel';
import { PlatformUsagePreviewDialog } from '../components/PlatformUsagePreviewDialog';
import { RunFeedbackButtons } from '../components/RunFeedbackButtons';
import { RunFeedbackList } from '../components/RunFeedbackList';
import { PlatformRequestId } from '../components/PlatformRequestId';
import { PageBreadcrumbs } from '../components/PageBreadcrumbs';
import { CopyId } from '../components/CopyId';
import { RelativeTime } from '../components/RelativeTime';
import { usePageTitle } from '../utils/pageTitle';
import { isAdminUser } from '../utils/auth';
import { describeRunModel } from '../config/modelConfig';
import { isRunSearchActive, nodeMatchesQuery, valueContains } from '../utils/chainRunSearch';

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  if (ms < 3_600_000) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(ms / 3_600_000);
  const rm = Math.round((ms % 3_600_000) / 60_000);
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
};

type ChainExecution = {
  id: string;
  chain_id: string;
  customer_id: string;
  status: string;
  input_data: any;
  node_results?: Record<string, any>;
  error?: string;
  started_at?: string;
  completed_at?: string;
  execution_time_ms?: number;
  total_tokens?: number;
  total_cost?: number;
  source_execution_id?: string;
  resume_from_node?: string;
  adjustment_notes?: string;
  pause_before_nodes?: string[];
  paused_at_node?: string;
  content_redacted?: boolean;
  redaction_reason?: string | null;
  passio_request_id?: string | null;
};

const collectImageSources = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectImageSources(item));
  }
  if (typeof value !== 'object') return [];

  const direct = [value.url, value.image_url, value.public_url, value.data].filter(
    (item): item is string => typeof item === 'string' && item.length > 0
  );

  const nested = ['images', 'results', 'output'].flatMap((key) => collectImageSources(value[key]));
  return [...direct, ...nested];
};

export const ChainRunDetails: React.FC = () => {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  const { user, permissions } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [execution, setExecution] = useState<ChainExecution | null>(null);
  const [feedbackVersion, setFeedbackVersion] = useState(0);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(300);
  const isDraggingRef = useRef(false);
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);
  const [rerunNodeId, setRerunNodeId] = useState<string | null>(null);
  const [rerunNotes, setRerunNotes] = useState('');
  const [platformPreviewOpen, setPlatformPreviewOpen] = useState(false);
  const [inRunSearch, setInRunSearch] = useState('');
  const [showMatchingStepsOnly, setShowMatchingStepsOnly] = useState(false);
  const isPlatformAdmin = isAdminUser(permissions?.userType);
  usePageTitle('Chain run');

  const nodeEntries = useMemo(() => {
    const nr = execution?.node_results || {};
    return Object.entries(nr).sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [execution?.node_results]);

  const searchingSteps = isRunSearchActive(inRunSearch);
  const matchingStepIds = useMemo(() => {
    if (!searchingSteps) return new Set<string>();
    const q = inRunSearch.trim().toLowerCase();
    return new Set(
      nodeEntries
        .filter(([nodeId, node]) => nodeId.toLowerCase().includes(q) || nodeMatchesQuery(node, inRunSearch))
        .map(([nodeId]) => nodeId)
    );
  }, [nodeEntries, searchingSteps, inRunSearch]);
  const visibleNodeEntries = searchingSteps && showMatchingStepsOnly
    ? nodeEntries.filter(([nodeId]) => matchingStepIds.has(nodeId))
    : nodeEntries;
  const inputMatchesSearch = searchingSteps && valueContains(execution?.input_data, inRunSearch);

  const totalCredits = useMemo(() => {
    const nr = execution?.node_results || {};
    return Object.values(nr).reduce((sum: number, node: any) => {
      const v = Number(node?.aiCreditsUsed ?? 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [execution?.node_results]);

  // Persist a fresh batch-status result into the selected node's output so the
  // special-case panel updates in place (used by the "Check now" / auto-poll).
  const updateNodeOutput = useCallback((nodeId: string, freshOutput: any) => {
    setExecution((prev) => {
      if (!prev?.node_results?.[nodeId]) return prev;
      return {
        ...prev,
        node_results: {
          ...prev.node_results,
          [nodeId]: { ...prev.node_results[nodeId], output: freshOutput },
        },
      };
    });
  }, []);

  const getIdToken = useCallback(async () => user?.getIdToken(), [user]);

  useEffect(() => {
    const load = async () => {
      if (!user || !executionId) return;
      setLoading(true);
      setError(null);
      try {
        const idToken = await user.getIdToken();
        const res = await fetch(`/api/v2/chains/executions/${executionId}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) throw new Error(await res.text());
        const json = await res.json();
        setExecution(json);
        const entries = Object.entries(json.node_results || {});
        if (entries.length > 0) setSelectedNode(entries[0][0]);
      } catch (e: any) {
        setError(e?.message || 'Failed to load chain execution');
        setExecution(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, executionId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !execution) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Chain execution not found'}
        </Alert>
      </Box>
    );
  }

  const ok = execution.status === 'completed';
  const activeResult = selectedNode ? execution.node_results?.[selectedNode] : null;
  const activeOutput = activeResult?.output;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <PageBreadcrumbs
            items={[
              { label: 'Runs & Performance', to: '/dashboard/function-runs' },
              ...(execution.chain_id ? [{ label: 'Chain performance', to: `/dashboard/chain-performance/${execution.chain_id}` }] : []),
              { label: 'Run' },
            ]}
          />
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Chain Run Details
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', color: 'text.secondary' }}>
              <CopyId label="Run" value={execution.id} short />
              {execution.started_at && <RelativeTime value={execution.started_at} />}
            </Box>
          </Box>
        </Box>
        {execution.id && (
          <RunFeedbackButtons sourceType="chain_execution" sourceId={execution.id} onChange={() => setFeedbackVersion((v) => v + 1)} />
        )}
        <Chip
          icon={ok ? <SuccessIcon /> : <ErrorIcon />}
          label={ok ? 'Success' : execution.status}
          color={ok ? 'success' : 'error'}
          size="small"
        />
        {execution.execution_time_ms && (
          <Chip label={formatDuration(execution.execution_time_ms)} size="small" variant="outlined" />
        )}
        {totalCredits > 0 && (
          <Chip label={`${totalCredits.toFixed(2)} credits`} size="small" variant="outlined" />
        )}
        {isPlatformAdmin && execution.id && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CalculateIcon />}
            onClick={() => setPlatformPreviewOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Convert to Platform Token Usage
          </Button>
        )}
      </Box>

      {execution.error && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }}>{execution.error}</Alert>
      )}
      {execution.content_redacted && (
        <Alert severity="info" sx={{ mx: 3, mt: 1 }}>
          {execution.redaction_reason === 'zdr_request'
            ? 'This run was made with zero data retention, so its input and step results were removed after the result was picked up.'
            : "Because of this account's data retention setting, this run's input and step results were removed."}{' '}
          Status, timing and usage are still recorded, but the run can't be rerun.
        </Alert>
      )}
      {execution.source_execution_id && (
        <Alert severity="info" icon={<ReplayIcon fontSize="small" />} sx={{ mx: 3, mt: 1, py: 0.5 }} variant="outlined">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Rerun from step: <strong>{execution.resume_from_node || 'unknown'}</strong>
            </Typography>
            {execution.adjustment_notes && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Adjustment note: &ldquo;{execution.adjustment_notes}&rdquo;
              </Typography>
            )}
          </Box>
        </Alert>
      )}
      <PlatformRequestId
        requestId={execution.passio_request_id}
        customerId={execution.customer_id !== permissions?.customerId ? execution.customer_id : null}
        sx={{ mx: 3, mt: 1 }}
      />
      {execution.id && (
        <RunFeedbackList
          sourceType="chain_execution"
          sourceId={execution.id}
          customerId={execution.customer_id !== permissions?.customerId ? execution.customer_id : null}
          refreshKey={feedbackVersion}
          sx={{ mx: 3, mt: 1, flexShrink: 0 }}
        />
      )}

      {/* Split panel layout */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* LEFT PANEL: Steps */}
        <Box sx={{
          width: leftPanelWidth, minWidth: 200, maxWidth: 450, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          borderRight: '1px solid', borderColor: 'divider',
          bgcolor: 'grey.50',
          overflow: 'hidden',
        }}>
          <Box sx={{ px: 1, pt: 1, pb: 0.5, flexShrink: 0 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Find in this run…"
              value={inRunSearch}
              onChange={(e) => setInRunSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />,
                endAdornment: inRunSearch ? (
                  <IconButton size="small" onClick={() => { setInRunSearch(''); setShowMatchingStepsOnly(false); }} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                ) : null,
              }}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem', bgcolor: 'background.paper' } }}
            />
            {searchingSteps && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {matchingStepIds.size} step{matchingStepIds.size === 1 ? '' : 's'} match
                  {inputMatchesSearch ? ' · input matches' : ''}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showMatchingStepsOnly}
                      onChange={(_, v) => setShowMatchingStepsOnly(v)}
                      sx={{ py: 0, pr: 0.5 }}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Matches only</Typography>}
                  sx={{ mr: 0, ml: 0 }}
                />
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', py: 1, pt: 0.5 }}>
            {visibleNodeEntries.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchingSteps ? 'No steps match this search.' : 'No steps recorded.'}
                </Typography>
              </Box>
            ) : (
              visibleNodeEntries.map(([nodeId, node]) => {
                const isSelected = selectedNode === nodeId;
                const stepStatus = node?.status || 'unknown';
                const isResumePoint = execution.resume_from_node === nodeId;
                const isMatch = matchingStepIds.has(nodeId);
                const displayIdx = nodeEntries.findIndex(([id]) => id === nodeId);

                return (
                  <Box
                    key={nodeId}
                    onClick={() => setSelectedNode(nodeId)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 1.5, py: 0.75, mx: 0.5, mb: 0.25, borderRadius: 1.5,
                      cursor: 'pointer',
                      opacity: searchingSteps && !isMatch && !isSelected ? 0.55 : 1,
                      bgcolor: isSelected ? 'primary.100' : isMatch ? 'warning.50' : 'transparent',
                      border: '2px solid',
                      borderColor: isSelected ? 'primary.main' : isMatch ? 'warning.light' : 'transparent',
                      boxShadow: isSelected ? '0 0 0 1px rgba(25,118,210,0.2)' : 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.100' : 'grey.100',
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        opacity: 1,
                      },
                    }}
                  >
                    {stepStatus === 'completed' && <Chip label="✓" size="small" color="success" sx={{ width: 20, height: 20, '& .MuiChip-label': { px: 0 } }} />}
                    {stepStatus === 'failed' && <Chip label="✗" size="small" color="error" sx={{ width: 20, height: 20, '& .MuiChip-label': { px: 0 } }} />}
                    {stepStatus !== 'completed' && stepStatus !== 'failed' && (
                      <Chip label="⋯" size="small" color="default" sx={{ width: 20, height: 20, '& .MuiChip-label': { px: 0 } }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? 'primary.dark' : 'text.primary',
                        display: 'block', lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{displayIdx + 1}.</Typography>
                        {nodeId}
                        {isMatch && (
                          <Chip label="match" size="small" color="warning" sx={{ height: 16, ml: 0.5, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }} />
                        )}
                        {isResumePoint && (
                          <Tooltip title={execution.adjustment_notes ? `Rerun point — Note: ${execution.adjustment_notes}` : 'Rerun started from this step'} arrow>
                            <ReplayIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: 'info.main' }} />
                          </Tooltip>
                        )}
                        {node?.knowledge && node.knowledge.learned + node.knowledge.category > 0 && (
                          <Tooltip
                            title={`Received ${node.knowledge.learned} learned and ${node.knowledge.category} knowledge-category snippets`}
                            arrow
                          >
                            <MenuBookIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: node.knowledge.learned ? 'primary.main' : 'text.secondary' }} />
                          </Tooltip>
                        )}
                      </Typography>
                      {node?.executionTime && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {formatDuration(node.executionTime)}
                          {typeof node?.aiCreditsUsed === 'number' && ` • ${node.aiCreditsUsed.toFixed(2)} cr`}
                        </Typography>
                      )}
                      {(() => {
                        const modelInfo = describeRunModel(node?.modelUsed, node?.modelRequested, node?.fallbackUsed);
                        if (!modelInfo.label) return null;
                        return (
                          <Tooltip title={modelInfo.tooltip || modelInfo.label} arrow>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {modelInfo.label}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                      {isResumePoint && execution.adjustment_notes && (
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'info.main', display: 'block', lineHeight: 1.2, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Note: {execution.adjustment_notes}
                        </Typography>
                      )}
                    </Box>
                    {(execution.status === 'completed' || execution.status === 'failed' || execution.status === 'cancelled') && (
                      <Tooltip title="Rerun from this step" arrow>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRerunNodeId(nodeId);
                            const isResumePoint = execution.resume_from_node === nodeId;
                            setRerunNotes(isResumePoint && execution.adjustment_notes ? execution.adjustment_notes : '');
                            setRerunDialogOpen(true);
                          }}
                          sx={{
                            minWidth: 0, px: 0.75, py: 0.25,
                            fontSize: '0.65rem', textTransform: 'none',
                            lineHeight: 1.2,
                            '& .MuiButton-startIcon': { mr: 0.25 },
                          }}
                        >
                          Rerun
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                );
              })
            )}
          </Box>

          {/* Chain input accordion at bottom */}
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1 }}>
            <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="caption" fontWeight={600}>Chain Input</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
                <JsonViewer data={execution.input_data} highlight={inRunSearch} />
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>

        {/* DRAG HANDLE */}
        <Box
          sx={{ width: 4, cursor: 'col-resize', bgcolor: 'divider', '&:hover': { bgcolor: 'primary.main' }, flexShrink: 0, transition: 'background-color 0.15s' }}
          onMouseDown={(e) => {
            e.preventDefault();
            isDraggingRef.current = true;
            const startX = e.clientX;
            const startW = leftPanelWidth;
            const onMove = (ev: MouseEvent) => { if (isDraggingRef.current) setLeftPanelWidth(Math.max(200, Math.min(450, startW + ev.clientX - startX))); };
            const onUp = () => { isDraggingRef.current = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        />

        {/* RIGHT PANEL: Selected step details */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
          {selectedNode && activeResult ? (() => {
            const output = activeOutput;
            const result = activeResult;
            const status = result?.status || 'unknown';

            const htmlResultUrl = (() => {
              if (!output) return null;
              const candidate = output.html_url || output.url;
              if (typeof candidate === 'string' && candidate.startsWith('http') && candidate.toLowerCase().includes('.html')) return candidate;
              return null;
            })();

            const imageSources = collectImageSources(output);

            return (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>{selectedNode}</Typography>
                  <Chip label={status} size="small" color={status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'default'} />
                  {result.executionTime && (
                    <Typography variant="caption" color="text.secondary">{formatDuration(result.executionTime)}</Typography>
                  )}
                  {typeof result?.aiCreditsUsed === 'number' && (
                    <Chip label={`${result.aiCreditsUsed.toFixed(2)} credits`} size="small" variant="outlined" />
                  )}
                  {(() => {
                    const modelInfo = describeRunModel(result?.modelUsed, result?.modelRequested, result?.fallbackUsed);
                    if (!modelInfo.label) return null;
                    return (
                      <Tooltip title={modelInfo.tooltip || modelInfo.label} arrow>
                        <Chip label={modelInfo.label} size="small" variant="outlined" />
                      </Tooltip>
                    );
                  })()}
                </Box>

                {execution.resume_from_node === selectedNode && execution.adjustment_notes && (
                  <Alert severity="info" icon={<NoteAddIcon fontSize="small" />} sx={{ mb: 2 }} variant="filled">
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                      Adjustment Note (from rerun)
                    </Typography>
                    <Typography variant="body2">{execution.adjustment_notes}</Typography>
                  </Alert>
                )}

                {result.error && <Alert severity="error" sx={{ mb: 2 }}>{result.error}</Alert>}

                {result.reliability && (
                  <Box sx={{ mb: 2 }}>
                    <ReliabilityTraceView trace={result.reliability} title="Reliability for this step" />
                  </Box>
                )}

                {result.knowledge && (
                  <Box sx={{ mb: 2 }}>
                    <KnowledgeTraceView trace={result.knowledge} title="Knowledge this step received" />
                  </Box>
                )}

                {imageSources.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    {imageSources.map((src, idx) => (
                      <Box key={`img-${idx}`} sx={{ maxWidth: 280 }}>
                        <Box component="img" src={src} alt={`${selectedNode} output ${idx + 1}`}
                          sx={{ width: '100%', height: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {htmlResultUrl && (
                  <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', borderColor: 'primary.main', borderWidth: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'primary.100' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1, color: 'primary.main' }}>Generated HTML</Typography>
                      <Button size="small" variant="contained" onClick={() => window.open(htmlResultUrl, '_blank')} sx={{ textTransform: 'none' }}>Open in New Tab</Button>
                      <Button size="small" variant="contained" color="secondary" onClick={() => {
                        const htmlContent = output?.html_content || output?.html || result?.input?.html_content || result?.input?.html;
                        if (htmlContent && typeof htmlContent === 'string' && htmlContent.length > 50) {
                          localStorage.setItem('grapesjs-html-content', htmlContent);
                        } else {
                          localStorage.setItem('grapesjs-html-content', '');
                        }
                        window.open('/dashboard/html-editor', '_blank');
                      }} sx={{ textTransform: 'none' }}>Edit in Editor</Button>
                      <Button size="small" variant="outlined" onClick={() => navigator.clipboard.writeText(htmlResultUrl)} sx={{ textTransform: 'none' }}>Copy URL</Button>
                    </Box>
                    <Box sx={{ width: '100%', height: 400, bgcolor: 'grey.50' }}>
                      <iframe title="HTML Result Preview" src={htmlResultUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                    </Box>
                  </Paper>
                )}

                {isBatchStatusOutput(output) && selectedNode ? (
                  <>
                    <BatchStatusPanel
                      key={selectedNode}
                      output={output}
                      nodeInput={result?.input}
                      getToken={getIdToken}
                      onUpdate={(fresh) => updateNodeOutput(selectedNode, fresh)}
                    />
                    <Accordion sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Raw output</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <JsonViewer data={output} highlight={inRunSearch} />
                      </AccordionDetails>
                    </Accordion>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Output</Typography>
                    <JsonViewer data={output} highlight={inRunSearch} />
                  </>
                )}

                {result?.input && (
                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Input</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <JsonViewer data={result.input} highlight={inRunSearch} />
                    </AccordionDetails>
                  </Accordion>
                )}

                {result?.tokenUsage && (
                  <Accordion sx={{ mt: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Token Usage</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <JsonViewer data={result.tokenUsage} />
                    </AccordionDetails>
                  </Accordion>
                )}

                <GroundingMetadataPanel metadata={result?.metadata} />
              </Box>
            );
          })() : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
              <Typography variant="body2">Select a step to view its details</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Rerun Dialog */}
      <Dialog open={rerunDialogOpen} onClose={() => setRerunDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReplayIcon color="primary" />
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>Rerun from Step</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A new execution will copy prior results and re-execute from <strong>{rerunNodeId}</strong> onwards.
          </Typography>
          <TextField
            fullWidth multiline minRows={3} maxRows={8}
            label="Adjustment Notes (optional)"
            placeholder="Describe what to do differently..."
            value={rerunNotes}
            onChange={(e) => setRerunNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRerunDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<ReplayIcon />} onClick={async () => {
            if (!execution?.id || !rerunNodeId) return;
            try {
              const token = await user?.getIdToken();
              const res = await fetch(`/api/v2/chains/executions/${execution.id}/rerun-from/${rerunNodeId}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ adjustment_notes: rerunNotes || null }),
              });
              if (!res.ok) { const err = await res.json().catch(() => ({})); alert(err.detail || 'Rerun failed'); return; }
              const newExec = await res.json();
              setRerunDialogOpen(false);
              setRerunNotes('');
              navigate(`/dashboard/chains/runs/${newExec.id}`);
            } catch (e) { console.error('Rerun failed:', e); }
          }}>Start Rerun</Button>
        </DialogActions>
      </Dialog>
      <PlatformUsagePreviewDialog
        open={platformPreviewOpen}
        onClose={() => setPlatformPreviewOpen(false)}
        title="Platform Chain Usage Preview"
        endpoint={execution.id ? `/api/v2/analytics/admin/chains/${execution.id}/platform-usage-preview` : null}
      />
    </Box>
  );
};
